import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { publicApi, anonymousQuizStorage } from '../services/publicApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TurndownService from 'turndown';
import PublicQuiz from './PublicQuiz';
import './LearnPage.css';

const LearnPage = () => {
  const { letterId } = useParams();
  const navigate = useNavigate();

  const [letters, setLetters] = useState([]);
  const [languageNotice, setLanguageNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Letter viewer state
  const [letterContent, setLetterContent] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);

  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [activeResultTab, setActiveResultTab] = useState('summary');
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  const currentLetterId = letterId ? parseInt(letterId) : null;

  // Fetch public letters list
  useEffect(() => {
    const fetchLetters = async () => {
      try {
        setLoading(true);
        const data = await publicApi.getPublicLetters();
        setLetters(data.letters || []);
        setLanguageNotice(data.language_notice);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLetters();
  }, []);

  // Load letter content when letterId changes
  useEffect(() => {
    if (!currentLetterId) {
      setSelectedLetter(null);
      setLetterContent('');
      return;
    }

    const letter = letters.find(l => l.letter_id === currentLetterId);
    if (letter) {
      setSelectedLetter(letter);
      loadLetterContent(currentLetterId);
    }
  }, [currentLetterId, letters]);

  const cleanLetterText = (htmlContent) => {
    return htmlContent
      .replace(/<style[^>]*>.*?<\/style>/gis, '')
      .replace(/<script[^>]*>.*?<\/script>/gis, '')
      .replace(/<!DOCTYPE[^>]*>/gi, '')
      .replace(/<html[^>]*>/gi, '')
      .replace(/<\/html>/gi, '')
      .replace(/<head[^>]*>.*?<\/head>/gis, '')
      .replace(/<body[^>]*>/gi, '')
      .replace(/<\/body>/gi, '')
      .replace(/<div[^>]*class="[^"]*metadata[^"]*"[^>]*>.*?<\/div>/gis, '')
      .replace(/<div[^>]*class="[^"]*edit-notice[^"]*"[^>]*>.*?<\/div>/gis, '')
      .replace('<hr>', '');
  };

  const loadLetterContent = async (id) => {
    setLoadingContent(true);
    try {
      const url = publicApi.getPublicLetterUrl(id);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load letter');
      const htmlContent = await response.text();

      const contentMatch = htmlContent.match(/<div[^>]*class="content"[^>]*>(.*?)<\/div>/gis);

      if (contentMatch && contentMatch[0]) {
        const rawContent = contentMatch[0]
          .replace(/<div[^>]*class="content"[^>]*>/gi, '')
          .replace(/<\/div>/gi, '')
          .replace(/contenteditable="true"/gi, '')
          .trim();

        setLetterContent(rawContent);
      } else {
        const turndownService = new TurndownService({
          headingStyle: 'atx',
          codeBlockStyle: 'fenced'
        });
        const cleanedHtml = cleanLetterText(htmlContent);
        const markdownContent = turndownService.turndown(cleanedHtml);
        setLetterContent(markdownContent);
      }
    } catch (err) {
      setLetterContent('Failed to load letter content.');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleLetterClick = (letter) => {
    navigate(`/learn/${letter.letter_id}`);
    setShowQuiz(false);
    setQuizResults(null);
    setActiveResultTab('summary');
  };

  const handleStartQuiz = () => {
    setShowQuiz(true);
    setQuizResults(null);
    setActiveResultTab('summary');
    setCurrentReviewIndex(0);
  };

  const handleQuizComplete = (results) => {
    setQuizResults(results);
    setShowQuiz(false);

    anonymousQuizStorage.save({
      quiz_id: results.quiz_id,
      letter_id: results.letter_id || 1,
      score: results.score,
      correct_answers: results.correct_answers,
      total_questions: results.total_questions,
      feedback: results.feedback,
      answers: results.answers || {},
      time_taken: results.time_taken || 0,
      completed_at: new Date().toISOString()
    });
  };

  const handleBackToList = () => {
    navigate('/learn');
    setShowQuiz(false);
    setQuizResults(null);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getPerformanceMessage = (score) => {
    if (score >= 90) return 'Excellent work! You have mastered these concepts.';
    if (score >= 80) return 'Great job! You have a solid understanding.';
    if (score >= 70) return 'Good work! Review the areas you missed.';
    if (score >= 60) return 'Not bad! Consider reviewing the material.';
    return 'Keep studying! Review the concepts and try again.';
  };

  // Helper to get feedback for a question
  const getQuestionFeedback = (questionId) => {
    if (!quizResults?.feedback) return null;
    return quizResults.feedback[questionId.toString()];
  };

  // Render listing page
  if (!currentLetterId) {
    return (
      <div className="learn-container">
        <div className="learn-header">
          <h1>Free PrepLetters</h1>
          <p className="learn-subtitle">
            Start your coding interview prep with our first 5 letters — no signup required.
          </p>
          {languageNotice && (
            <div className="language-notice">
              <i className="bi bi-globe"></i>
              {languageNotice}
            </div>
          )}
        </div>

        {loading ? (
          <div className="learn-loading">
            <div className="spinner-modern"></div>
            <p>Loading letters...</p>
          </div>
        ) : error ? (
          <div className="learn-error">
            <p>{error}</p>
          </div>
        ) : (
          <div className="learn-grid">
            {letters.map((letter) => (
              <div
                key={letter.letter_id}
                className="learn-card"
                onClick={() => handleLetterClick(letter)}
              >
                <div className="learn-card-number">#{letter.letter_id}</div>
                <div className="learn-card-content">
                  <h3>{letter.title}</h3>
                  <span className="learn-card-language">{letter.language}</span>
                </div>
                <div className="learn-card-actions">
                  {letter.has_quiz && (
                    <span className="learn-card-quiz-badge">
                      <i className="bi bi-lightning-charge-fill"></i> Quiz Available
                    </span>
                  )}
                  <span className="learn-card-arrow">
                    <i className="bi bi-arrow-right"></i>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="learn-cta-section">
          <div className="learn-cta-card">
            <h3>Want access to all 100+ letters?</h3>
            <p>Sign up to unlock PrepLetters in Python, JavaScript, Java, and C++, plus interactive quizzes and progress tracking.</p>
            <div className="learn-cta-buttons">
              <Link to="/login" className="learn-cta-primary">
                Sign Up Free
              </Link>
              <Link to="/login" className="learn-cta-secondary">
                Already have an account? Log in
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show quiz (hide letter content)
  if (showQuiz) {
    return (
      <div className="learn-container">
        <div className="learn-viewer-header">
          <button className="learn-back-btn" onClick={() => setShowQuiz(false)}>
            <i className="bi bi-arrow-left"></i> Back to Letter
          </button>
          <div className="learn-viewer-title">
            <span className="learn-viewer-number">Letter #{currentLetterId}</span>
            {selectedLetter && <h2>{selectedLetter.title} — Quiz</h2>}
          </div>
        </div>
        <PublicQuiz
          letterId={currentLetterId}
          onComplete={handleQuizComplete}
          onCancel={() => setShowQuiz(false)}
        />
      </div>
    );
  }

  // Show results (hide letter content)
  if (quizResults) {
    return (
      <div className="learn-container">
        <div className="learn-viewer-header">
          <button className="learn-back-btn" onClick={() => setQuizResults(null)}>
            <i className="bi bi-arrow-left"></i> Back to Letter
          </button>
          <div className="learn-viewer-title">
            <span className="learn-viewer-number">Letter #{currentLetterId}</span>
            {selectedLetter && <h2>{selectedLetter.title} — Results</h2>}
          </div>
          <div className="learn-viewer-actions">
            <button className="learn-retake-btn" onClick={handleStartQuiz}>
              <i className="bi bi-arrow-repeat"></i> Retake
            </button>
          </div>
        </div>

        {/* Results Header Card */}
        <div className="learn-results-card">
          <div className="learn-results-header">
            <h3>Quiz Results</h3>
            <span className="learn-results-badge" style={{ background: `${getScoreColor(quizResults.score)}20`, color: getScoreColor(quizResults.score) }}>
              {quizResults.score >= 80 ? 'Excellent!' : quizResults.score >= 60 ? 'Good job!' : 'Keep practicing!'}
            </span>
          </div>

          <div className="learn-results-hero">
            <div className="learn-results-score-block">
              <div className="score-circle" style={{ borderColor: getScoreColor(quizResults.score) }}>
                <span className="score-number">{Math.round(quizResults.score)}%</span>
              </div>
              <div className="score-label">{quizResults.correct_answers}/{quizResults.total_questions} Correct</div>
              <div className="score-progress-bar wide">
                <div className="score-progress-fill" style={{ width: `${quizResults.score}%`, background: getScoreColor(quizResults.score) }}></div>
              </div>
            </div>

            <div className="learn-results-info">
              <div className="info-pill">
                <i className="bi bi-graph-up"></i>
                <span>{getPerformanceMessage(quizResults.score)}</span>
              </div>
              {quizResults.quiz_statistics && (
                <div className="info-pill">
                  <i className="bi bi-globe"></i>
                  <span>Global Avg: {Math.round(quizResults.quiz_statistics.average_score)}%</span>
                </div>
              )}
              <div className="info-pill">
                <i className="bi bi-calendar-check"></i>
                <span>{new Date().toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed Results */}
        <div className="learn-results-card">
          <div className="learn-results-tabs">
            <button
              className={`learn-result-tab ${activeResultTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveResultTab('summary')}
            >
              <i className="bi bi-bar-chart"></i> Summary
            </button>
            <button
              className={`learn-result-tab ${activeResultTab === 'review' ? 'active' : ''}`}
              onClick={() => setActiveResultTab('review')}
            >
              <i className="bi bi-list-check"></i> Question Review
            </button>
            <button
              className={`learn-result-tab ${activeResultTab === 'share' ? 'active' : ''}`}
              onClick={() => setActiveResultTab('share')}
            >
              <i className="bi bi-share"></i> Share
            </button>
          </div>

          <div className="learn-results-tab-content">
            {/* Summary Tab */}
            {activeResultTab === 'summary' && (
              <div className="learn-summary-tab">
                {/* Pattern Analysis */}
                {quizResults.feedback && (
                  <div className="learn-pattern-analysis">
                    <h5>Pattern Analysis</h5>
                    <div className="pattern-grid">
                      {(() => {
                        const patternStats = {};
                        Object.entries(quizResults.feedback).forEach(([qId, fb]) => {
                          const patterns = fb.patterns_tested || ['General'];
                          patterns.forEach(pattern => {
                            if (!patternStats[pattern]) {
                              patternStats[pattern] = { total: 0, correct: 0 };
                            }
                            patternStats[pattern].total++;
                            if (fb.correct) patternStats[pattern].correct++;
                          });
                        });

                        return Object.entries(patternStats).map(([pattern, stats]) => {
                          const pct = Math.round((stats.correct / stats.total) * 100);
                          return (
                            <div key={pattern} className="pattern-item">
                              <div className="pattern-header">
                                <span className="pattern-name">{pattern.replace(/_/g, ' ')}</span>
                                <span className="pattern-score">{pct}%</span>
                              </div>
                              <div className="pattern-bar">
                                <div className="pattern-bar-fill" style={{ width: `${pct}%`, background: getScoreColor(pct) }}></div>
                              </div>
                              <span className="pattern-detail">{stats.correct}/{stats.total} correct</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

                {/* Recommendation */}
                {quizResults.score < 70 && (
                  <div className="learn-recommendation">
                    <i className="bi bi-lightbulb"></i>
                    <div>
                      <strong>Recommendation:</strong> Review the letter and try again to improve your understanding.
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="learn-results-cta">
                  <Link to="/login" className="learn-cta-primary">
                    Sign up to save results & track progress
                  </Link>
                </div>
              </div>
            )}

            {/* Question Review Tab */}
            {activeResultTab === 'review' && quizResults.feedback && (
              <div className="learn-review-tab">
                <div className="review-navigation">
                  <button
                    className="review-nav-btn"
                    disabled={currentReviewIndex === 0}
                    onClick={() => setCurrentReviewIndex(prev => prev - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  <span className="review-counter">
                    {currentReviewIndex + 1} / {Object.keys(quizResults.feedback).length}
                  </span>
                  <button
                    className="review-nav-btn"
                    disabled={currentReviewIndex >= Object.keys(quizResults.feedback).length - 1}
                    onClick={() => setCurrentReviewIndex(prev => prev + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </div>

                {(() => {
                  const entries = Object.entries(quizResults.feedback);
                  const [qId, fb] = entries[currentReviewIndex] || [];
                  if (!fb) return null;

                  return (
                    <div className="review-question-card">
                      <div className="review-question-header">
                        <span className={`review-status ${fb.correct ? 'correct' : 'incorrect'}`}>
                          {fb.correct ? '✓ Correct' : '✗ Incorrect'}
                        </span>
                      </div>

                      <p className="review-question-text">{fb.question_text || `Question ${qId}`}</p>

                      <div className="review-answers">
                        <div className="review-answer">
                          <span className="answer-label">Your Answer:</span>
                          <span className={`answer-value ${fb.correct ? 'correct' : 'incorrect'}`}>
                            {Array.isArray(fb.user_answer) ? fb.user_answer.join(', ') : fb.user_answer || 'Not answered'}
                          </span>
                        </div>
                        {!fb.correct && (
                          <div className="review-answer">
                            <span className="answer-label">Correct Answer:</span>
                            <span className="answer-value correct">
                              {Array.isArray(fb.correct_answer) ? fb.correct_answer.join(', ') : fb.correct_answer}
                            </span>
                          </div>
                        )}
                      </div>

                      {fb.explanation && (
                        <div className="review-explanation">
                          <h6>Explanation</h6>
                          <p>{fb.explanation}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Share Tab */}
            {activeResultTab === 'share' && (
              <div className="learn-share-tab">
                <div className="learn-share-card" id="share-card">
                  <div className="share-card-inner">
                    <div className="share-card-logo">Prep<span>Letter</span></div>
                    <div className="share-card-tagline">Daily Interview Prep</div>
                    <div className="share-card-divider"></div>
                    <div className="share-card-quiz">
                      Letter #{currentLetterId}: {selectedLetter?.title || 'Quiz'}
                    </div>
                    <div className="share-card-score">{Math.round(quizResults.score)}%</div>
                    <div className="share-card-detail">
                      {quizResults.correct_answers}/{quizResults.total_questions} correct
                    </div>
                    <div className="share-card-divider"></div>
                    <div className="share-card-footer">
                      <span className="share-card-url">prepletter.app</span>
                    </div>
                  </div>
                </div>
                <div className="learn-share-buttons">
                  <button
                    className="share-btn share-linkedin"
                    onClick={() => {
                      const title = selectedLetter?.title || `#${currentLetterId}`;
                      const score = Math.round(quizResults.score);
                      const emoji = score >= 80 ? '🟢' : score >= 60 ? '🟡' : '🔴';
                      const text = encodeURIComponent(
                        `${emoji} PrepLetter Quiz Result\n━━━━━━━━━━━━━━━━━━\n📚 Letter #${currentLetterId}: ${title}\n🎯 Score: ${score}% (${quizResults.correct_answers}/${quizResults.total_questions} correct)\n━━━━━━━━━━━━━━━━━━\nPrepLetter helps you master coding interviews with daily practice letters and interactive quizzes.\n\n💻 Try it free → prepletter.app/learn\n\n#CodingInterview #LeetCode #TechJobs #Programming #SoftwareEngineering`
                      );
                      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=https://prepletter.app/learn&summary=${text}`, '_blank');
                    }}
                  >
                    <i className="bi bi-linkedin"></i> Share on LinkedIn
                  </button>
                  <button
                    className="share-btn share-copy"
                    onClick={() => {
                      const title = selectedLetter?.title || `#${currentLetterId}`;
                      const score = Math.round(quizResults.score);
                      const emoji = score >= 80 ? '🟢' : score >= 60 ? '🟡' : '🔴';
                      const text = `${emoji} PrepLetter Quiz Result\n━━━━━━━━━━━━━━━━━━\n📚 Letter #${currentLetterId}: ${title}\n🎯 Score: ${score}% (${quizResults.correct_answers}/${quizResults.total_questions} correct)\n━━━━━━━━━━━━━━━━━━\n💻 Try it free → prepletter.app/learn`;
                      navigator.clipboard.writeText(text);
                      alert('Copied to clipboard!');
                    }}
                  >
                    <i className="bi bi-clipboard"></i> Copy Link
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render letter viewer (default)
  return (
    <div className="learn-container">
      <div className="learn-viewer-header">
        <button className="learn-back-btn" onClick={handleBackToList}>
          <i className="bi bi-arrow-left"></i> Back to Letters
        </button>
        <div className="learn-viewer-title">
          <span className="learn-viewer-number">Letter #{currentLetterId}</span>
          {selectedLetter && <h2>{selectedLetter.title}</h2>}
        </div>
        <div className="learn-viewer-actions">
          {currentLetterId === 1 && (
            <button className="learn-quiz-btn" onClick={handleStartQuiz}>
              <i className="bi bi-lightning-charge-fill"></i> Take Quiz
            </button>
          )}
          {currentLetterId >= 2 && (
            <Link to="/login" className="learn-signup-btn">
              <i className="bi bi-unlock"></i> Sign up for quizzes
            </Link>
          )}
        </div>
      </div>

      {/* Letter Content */}
      <div className="learn-content-area">
        {loadingContent ? (
          <div className="learn-loading">
            <div className="spinner-modern"></div>
            <p>Loading letter...</p>
          </div>
        ) : (
          <div className="learn-letter-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {letterContent}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="learn-bottom-cta">
        <div className="learn-cta-card">
          <h3>Enjoying PrepLetter?</h3>
          <p>Sign up free to access all 100+ letters, take quizzes, and track your progress.</p>
          <Link to="/login" className="learn-cta-primary">
            Sign Up Free
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LearnPage;
